import classNames from 'classnames';
import { FieldProps, useFormikContext } from 'formik';
import { FocusEvent, ReactElement, useCallback } from 'react';
import UDFormFieldWrapper from 'ud-form/wrapper';
import { UDFormComponentProps } from '../types';
import UDSelect, { UDSelectProps } from './component';

type UDFormSelectProps<isMulti extends boolean> = UDFormComponentProps &
  UDSelectProps<isMulti> & {
    selectComponent?: (props: any) => ReactElement<any, any> | null;
  };

type Returning = <isMulti extends boolean = false>(props: UDFormSelectProps<isMulti>) => JSX.Element;

const UDFormSelect: Returning = (props) => {
  const { name, containerProps, required, label, labelProps, selectComponent, errorVisible, ...otherSelectProps } = props;

  const { setFieldValue, setFieldTouched } = useFormikContext();
  const onChange = useCallback(
    (value: any, isMulti: any) => {
      setFieldValue(name, value);

      if (props.onChange) {
        props.onChange(value, isMulti);
      }
    },
    [name, props, setFieldValue],
  );

  // Formik `field.onBlur` берёт имя поля из `event.target.name`, а у внутреннего input react-select
  // имени нет — поле так и не становилось «тронутым», и причина ошибки не показывалась никогда.
  const onBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setFieldTouched(name, true);
      props.onBlur?.(event);
    },
    [name, props, setFieldTouched],
  );

  const Component = selectComponent || UDSelect;

  return (
    <UDFormFieldWrapper
      name={name}
      containerProps={containerProps}
      label={label}
      labelProps={labelProps}
      required={required}
      errorVisible={errorVisible}
    >
      {({ field, meta }: FieldProps<any>) => (
        <Component
          {...otherSelectProps}
          {...field}
          divProps={{
            className: classNames('w-full'),
          }}
          className={classNames('w-full', otherSelectProps?.className)}
          onChange={onChange}
          onBlur={onBlur}
          // Как у поля ввода (`input.tsx`): рамка краснеет, когда поле тронули или заполнили, а не сразу.
          hasError={(meta.touched || hasValue(field.value)) && Boolean(meta.error)}
        />
      )}
    </UDFormFieldWrapper>
  );
};

const hasValue = (value: unknown) => (Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== '');

export default UDFormSelect;
